import { useEffect, useState } from 'react';
import type { GeoLine } from '@tiw/globe';

/**
 * Load a static GeoJSON (served from apps/web/public/data) and flatten its
 * LineString / MultiLineString features into a list of polylines for the
 * GeoLines renderer. Used for submarine cables and coastline outlines.
 * Fetched once per url; empty until loaded.
 */
interface GeoJSONLike {
  readonly features?: ReadonlyArray<{
    readonly geometry?: { readonly type?: string; readonly coordinates?: unknown };
  }>;
}

export function useGeoLines(url: string): readonly GeoLine[] {
  const [lines, setLines] = useState<readonly GeoLine[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<GeoJSONLike>) : Promise.reject(new Error(String(r.status)))))
      .then((geo) => {
        if (cancelled) return;
        const out: GeoLine[] = [];
        for (const feature of geo.features ?? []) {
          const geom = feature.geometry;
          if (!geom?.coordinates) continue;
          if (geom.type === 'LineString') {
            out.push(geom.coordinates as GeoLine);
          } else if (geom.type === 'MultiLineString') {
            for (const line of geom.coordinates as unknown[]) {
              out.push(line as GeoLine);
            }
          }
        }
        setLines(out);
      })
      .catch(() => {
        if (!cancelled) setLines([]);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return lines;
}
